import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "/api";

const getToken = () => localStorage.getItem("token");

const getUser = () => {
  try { return JSON.parse(localStorage.getItem("user") || "{}"); }
  catch { return {}; }
};

function CompanyDetails() {
  const params = useParams();
  const companyId = params.companyId || params.id;
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const token = getToken();
  const user  = getUser();

  const isSuperAdmin    = user?.role === "super_admin";
  const isSystemManager = user?.role === "system_manager";
  const canManage       = isSuperAdmin || isSystemManager;

  /* ================= FETCH DATA ================= */
  useEffect(() => {
    if (!companyId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch company and devices in parallel
        const [companyRes, deviceRes] = await Promise.all([
          fetch(`${API}/companies/${companyId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API}/devices`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!companyRes.ok) {
          const err = await companyRes.json().catch(() => ({}));
          throw new Error(err.message || "Company not found");
        }

        const companyData = await companyRes.json();
        setCompany(companyData);

        const deviceData = await deviceRes.json();
        const devicesList = Array.isArray(deviceData)
          ? deviceData
          : deviceData.rows || deviceData.data || [];

        // Filter to only this company's devices
        setDevices(
          devicesList.filter((d) => String(d.company_id) === String(companyId))
        );

      } catch (err) {
        console.error("Company details error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, token]);

  /* ================= STATES ================= */
  if (loading) return <div className="p-6 text-gray-500">Loading company...</div>;
  if (error)   return <div className="p-6 text-red-500">{error}</div>;
  if (!company) return <div className="p-6 text-red-500">Company not found</div>;

  /* ================= KPI ================= */
  const online      = devices.filter(d => d.status === "online").length;
  const offline     = devices.filter(d => d.status === "offline").length;
  const maintenance = devices.filter(d => d.status === "maintenance").length;

  return (
    <div className="p-6 space-y-6">

      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate("/dashboard/companies")}
            className="text-sm text-indigo-600 hover:underline mb-1 block"
          >
            ← Back to Companies
          </button>
          <h1 className="text-2xl font-bold text-slate-800">
            {company.company_name}
          </h1>
          <p className="text-sm text-slate-500">Company Overview & Devices</p>
        </div>

        {/* Edit only for super_admin / system_manager */}
        {canManage && (
          <button
            onClick={() => navigate(`/dashboard/companies/edit/${companyId}`)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-xl"
          >
            Edit Company
          </button>
        )}
      </div>

      {company.logo_data && (
        <div className="flex items-center gap-4 border-b border-slate-100 pb-5">
          <img src={company.logo_data} alt={`${company.company_name} logo`} className="h-16 w-16 rounded border border-slate-200 bg-white object-contain p-1" />
          <span className="text-sm font-medium text-slate-600">Company logo</span>
        </div>
      )}

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <Kpi title="Total Devices" value={devices.length}  color="bg-blue-500"   />
        <Kpi title="Online"        value={online}          color="bg-green-500"  />
        <Kpi title="Offline"       value={offline}         color="bg-red-500"    />
        <Kpi title="Maintenance"   value={maintenance}     color="bg-yellow-500" />
      </div>

      {/* COMPANY INFO */}
      <div className="bg-white p-6 rounded-2xl border shadow-sm">
        <h3 className="font-semibold mb-4">Company Info</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Info label="Name"     value={company.company_name} />
          <Info label="Type"     value={company.company_type || "-"} />
          <Info label="Location" value={company.location || "-"} />
          <Info label="Status"   value={<StatusBadge status={company.status} />} />
        </div>
      </div>

      {/* DEVICES */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Devices</h3>

          {canManage && (
            <button
              onClick={() => navigate("/dashboard/devices/add")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
            >
              + Add Device
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {devices.map((d) => {
            const deviceUid = d.device_uid || d.uid || d.deviceId;
            const openDevice = () => {
              if (deviceUid) {
                navigate(`/dashboard/devices/${encodeURIComponent(deviceUid)}`);
              }
            };

            return (
            <div
              key={d.id ?? deviceUid}
              onClick={openDevice}
              className={`p-4 rounded-xl border bg-white transition ${
                deviceUid
                  ? "cursor-pointer hover:shadow-md"
                  : "cursor-not-allowed opacity-60"
              }`}
              aria-disabled={!deviceUid}
            >
              <div className="font-semibold">{deviceUid || "No UID"}</div>
              <div className="text-xs text-gray-400 mt-1">
                {d.product_name || "No Product"}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <StatusDot status={d.status} />
                <StatusBadge status={d.status} />
              </div>
            </div>
            );
          })}

          {devices.length === 0 && (
            <div className="text-gray-400 col-span-full text-center py-6">
              No devices assigned to this company
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

/* ================= SUB-COMPONENTS ================= */

const Kpi = ({ title, value, color }) => (
  <div className={`p-5 rounded-xl text-white ${color}`}>
    <p className="text-sm opacity-80">{title}</p>
    <h2 className="text-2xl font-bold">{value}</h2>
  </div>
);

const Info = ({ label, value }) => (
  <div>
    <p className="text-gray-400 text-xs">{label}</p>
    <p className="font-semibold">{value}</p>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    online:      "bg-green-100 text-green-700",
    maintenance: "bg-yellow-100 text-yellow-700",
    offline:     "bg-red-100 text-red-600",
    active:      "bg-green-100 text-green-700",
    inactive:    "bg-gray-200 text-gray-600",
  };
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${map[status] || "bg-gray-100 text-gray-500"}`}>
      {status}
    </span>
  );
};

const StatusDot = ({ status }) => {
  const map = {
    online:      "bg-green-500",
    maintenance: "bg-yellow-500",
    offline:     "bg-red-500",
  };
  return <span className={`w-2 h-2 rounded-full inline-block ${map[status] || "bg-gray-400"}`} />;
};

export default CompanyDetails;
